/** Utilitários de texto puros — sem dependência de Node, o painel também usa. */

const HTML_TAG = /<[^>]*>/g;
const HTML_ENTITY = /&(nbsp|amp|lt|gt|quot|#39|apos);/g;
const ENTITY_MAP: Record<string, string> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  '#39': "'",
  apos: "'",
};

/** A `Description` da VTEX é HTML. Contagem de caracteres e regras de texto usam o conteúdo visível. */
export function stripHtml(input: string): string {
  return input
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(HTML_TAG, '')
    .replace(HTML_ENTITY, (_m, entity: string) => ENTITY_MAP[entity] ?? ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function visibleLength(input: string | null | undefined): number {
  if (!input) return 0;
  return stripHtml(input).length;
}

export function isBlank(value: string | null | undefined): boolean {
  return value === null || value === undefined || stripHtml(value).length === 0;
}

/** Minúsculas, sem acento, sem pontuação, espaços colapsados. Base de comparação e dedup. */
export function normalize(input: string): string {
  return stripHtml(input)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function tokenize(input: string): string[] {
  const normalized = normalize(input);
  return normalized.length === 0 ? [] : normalized.split(' ');
}

/** Similaridade de Jaccard sobre bigramas de caracteres. 0 a 1. */
export function similarity(a: string, b: string): number {
  const left = bigrams(normalize(a));
  const right = bigrams(normalize(b));
  if (left.size === 0 && right.size === 0) return 1;
  if (left.size === 0 || right.size === 0) return 0;

  let intersection = 0;
  for (const gram of left) {
    if (right.has(gram)) intersection += 1;
  }
  return intersection / (left.size + right.size - intersection);
}

function bigrams(value: string): Set<string> {
  const grams = new Set<string>();
  for (let i = 0; i < value.length - 1; i += 1) {
    grams.add(value.slice(i, i + 2));
  }
  return grams;
}

/**
 * Densidade do token mais repetido, ignorando stopwords e rótulos estruturais
 * de catálogo (ingredientes, especificações…). Base da heurística de keyword stuffing.
 */
export function topTokenDensity(input: string): { token: string; count: number; density: number } {
  const tokens = tokenize(input).filter(
    (t) => t.length > 3 && !PT_STOPWORDS.has(t) && !PT_CATALOG_STRUCTURAL_TOKENS.has(t),
  );
  if (tokens.length === 0) return { token: '', count: 0, density: 0 };

  const counts = new Map<string, number>();
  for (const token of tokens) {
    counts.set(token, (counts.get(token) ?? 0) + 1);
  }

  let bestToken = '';
  let bestCount = 0;
  for (const [token, count] of counts) {
    if (count > bestCount) {
      bestToken = token;
      bestCount = count;
    }
  }
  return { token: bestToken, count: bestCount, density: bestCount / tokens.length };
}

/**
 * Tokens elegíveis e em “shouting” (CAIXA ALTA), ignorando siglas curtas.
 * ALLCAPS com comprimento ≤ 5 (HDMI, OLED, WIFI…) não contam como ênfase.
 */
export function shoutingStats(input: string): {
  ratio: number;
  count: number;
  eligible: number;
} {
  const text = stripHtml(input);
  const words = text.split(/\s+/).filter((w) => w.length >= 4 && /[A-Za-zÀ-ÿ]/.test(w));
  if (words.length === 0) return { ratio: 0, count: 0, eligible: 0 };
  const shouting = words.filter(
    (w) => w.length > 5 && w === w.toUpperCase() && w !== w.toLowerCase(),
  );
  return {
    ratio: shouting.length / words.length,
    count: shouting.length,
    eligible: words.length,
  };
}

/** Proporção de palavras em CAIXA ALTA, ignorando siglas curtas (≤ 5 letras). */
export function shoutingRatio(input: string): number {
  return shoutingStats(input).ratio;
}

export function truncate(input: string, max: number): string {
  if (input.length <= max) return input;
  const cut = input.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd();
}

export const PT_STOPWORDS = new Set([
  'para',
  'com',
  'sem',
  'como',
  'esse',
  'essa',
  'este',
  'esta',
  'isso',
  'pelo',
  'pela',
  'mais',
  'menos',
  'muito',
  'pouco',
  'seus',
  'suas',
  'dele',
  'dela',
  'sobre',
  'entre',
  'quando',
  'onde',
  'porque',
  'todos',
  'todas',
  'cada',
  'sendo',
  'ainda',
  'apenas',
  'tambem',
  'ate',
  'que',
  'uma',
  'uns',
  'umas',
  'dos',
  'das',
  'nos',
  'nas',
  'aos',
]);

/**
 * Rótulos de seção/ficha de catálogo (já normalizados como `tokenize`).
 * Não contam para keyword stuffing — repetir o heading não é SEO stuffing.
 */
export const PT_CATALOG_STRUCTURAL_TOKENS = new Set([
  'ingredientes',
  'especificacoes',
  'caracteristicas',
  'composicao',
  'conteudo',
  'embalagem',
  'modo',
  'uso',
  'preparo',
  'conservacao',
  'advertencias',
  'beneficios',
  'detalhes',
  'informacoes',
  'produto',
  'descricao',
]);
